import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  DispatchDesk,
  DispatchListItem,
  DispatchSummary,
  TicketDispatch,
  TicketDispatchResponse,
  TicketDispatchRule,
  TicketDispatchRulePutBody,
  TicketDispatchStatus,
} from '@erp/shared';
import { api, apiList } from '@/lib/api-client';
import { TIMELINE_KEY } from '@/features/atendimentos/timeline-key';

/**
 * Disparo por ticket (spec 014): a regra e o painel da mesa, e o selo/ações do
 * ticket. Chaves sob ['erp', 'desks' | 'tickets', …] para que as invalidações
 * amplas que já existem (troca de origem da mesa, ações do ticket) as peguem.
 */
export function deskDispatchRuleOptions(deskId: number) {
  return queryOptions({
    queryKey: ['erp', 'desks', 'dispatch-rule', { deskId }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<TicketDispatchRule | null>(`/desks/${deskId}/dispatch-rule`, {
        signal,
      });
      if (res.error) throw res.error;
      return res.data ?? null;
    },
  });
}

export function deskDispatchSummaryOptions(deskId: number) {
  return queryOptions({
    queryKey: ['erp', 'desks', 'dispatch-summary', { deskId }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<DispatchSummary>(`/desks/${deskId}/dispatch-summary`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    // o ciclo roda a cada 5 min; o painel acompanha sem precisar recarregar
    refetchInterval: 60_000,
  });
}

export function useSaveDispatchRule(deskId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TicketDispatchRulePutBody) => {
      const res = await api<TicketDispatchRule>(`/desks/${deskId}/dispatch-rule`, {
        method: 'PUT',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['erp', 'desks', 'dispatch-rule', { deskId }] });
      void qc.invalidateQueries({ queryKey: ['erp', 'desks', 'dispatch-summary', { deskId }] });
    },
  });
}

export function ticketDispatchQuery(ticketNumber: number) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'dispatch', { ticketNumber }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<TicketDispatchResponse>(`/tickets/${ticketNumber}/dispatch`, {
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    // o selo muda por fora (ciclo, ack da Meta, resposta do cliente)
    refetchInterval: 60_000,
  });
}

/**
 * "Disparar agora" (POST) e "Tirar da fila" (DELETE) — a MESMA mutação nas
 * visões completa e compacta (o menu do useTicketDispatchMenu).
 */
export function useTicketDispatchAction(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (action: 'dispatch' | 'remove') => {
      const res = await api<TicketDispatch>(`/tickets/${ticketNumber}/dispatch`, {
        method: action === 'dispatch' ? 'POST' : 'DELETE',
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      // o disparo move o ticket e grava na timeline — a do Completo (events) e
      // a do atendimento compacto (timeline)
      void qc.invalidateQueries({ queryKey: ['erp', 'tickets', 'dispatch', { ticketNumber }] });
      void qc.invalidateQueries({ queryKey: ['erp', 'tickets', 'detail', { ticketNumber }] });
      void qc.invalidateQueries({ queryKey: ['erp', 'tickets', 'events', { ticketNumber }] });
      void qc.invalidateQueries({ queryKey: [...TIMELINE_KEY, { ticketNumber }] });
      // e muda a lista "Disparos" e o painel da mesa
      void qc.invalidateQueries({ queryKey: DISPATCH_LIST_KEY });
      void qc.invalidateQueries({ queryKey: ['erp', 'desks', 'dispatch-summary'] });
    },
  });
}

// ── a lista "Disparos" ──────────────────────────────────────────────────────

/** linhas por página da lista */
export const DISPATCH_LIST_PAGE_SIZE = 50;

/** prefixo das consultas da lista (a mutação acima o invalida) */
export const DISPATCH_LIST_KEY = ['erp', 'tickets', 'dispatches'] as const;

export interface DispatchListParams {
  from: string;
  to: string;
  desk?: number | undefined;
  status?: TicketDispatchStatus | undefined;
  /** 1… */
  page: number;
}

/** uma página da lista (GET /tickets/dispatches, total no X-Total-Items) */
export function dispatchListOptions(params: DispatchListParams) {
  return queryOptions({
    queryKey: [...DISPATCH_LIST_KEY, 'list', params] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<DispatchListItem>('/tickets/dispatches', {
        signal,
        query: {
          from: params.from,
          to: params.to,
          desk: params.desk,
          status: params.status,
          limit: DISPATCH_LIST_PAGE_SIZE,
          offset: (params.page - 1) * DISPATCH_LIST_PAGE_SIZE,
        },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    // a entrega e a resposta chegam por fora (o ack da Blip, o cliente)
    refetchInterval: 60_000,
  });
}

/** as mesas com regra de disparo que a pessoa enxerga (o filtro e os painéis) */
export function dispatchDesksOptions() {
  return queryOptions({
    queryKey: [...DISPATCH_LIST_KEY, 'desks'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<DispatchDesk[]>('/tickets/dispatches/desks', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}
